import { TreeNodeData } from "@tenforce/product-fields"
import {
    ValueWithGroup,
    SelectableValue,
} from "../../../../apis/treeSelector/types"
import { Type } from "../../../../apis/models/treeSelector/models"

export const initialTreeSelectorData = {
    data: [],
    totalCount: 0,
    currentSkip: 0,
    hasMore: false,
}

export function transformToTreeNodeData(
    backendArray: ValueWithGroup[],
    isGrouped: boolean,
): TreeNodeData<SelectableValue>[] {
    if (isGrouped) {
        const groupsMap = new Map<number, TreeNodeData<SelectableValue>>()
        const ungroupedItems: TreeNodeData<SelectableValue>[] = []

        backendArray.forEach((item) => {
            if (item.id) {
                const treeNode: TreeNodeData<SelectableValue> = {
                    id: `${item.id}`,
                    label: item.label,
                    data: {
                        id: item.id,
                        label: item.label,
                        isActive: item.isActive ?? true,
                    } as SelectableValue,
                    isSelected: true,
                    isSelectable: true,
                    isExpanded: true,
                    hasChildren: false,
                    childrenCount: 0,
                    children: undefined,
                }

                if (!item.groupData) {
                    ungroupedItems.push(treeNode)
                    return
                }

                const groupId = item.groupData.id
                const groupName = item.groupData.label

                if (!groupsMap.has(groupId)) {
                    groupsMap.set(groupId, {
                        id: `${groupId}`,
                        label: groupName,
                        data: {
                            id: groupId,
                            label: groupName,
                            isActive: true,
                        } as SelectableValue,
                        isSelected: false,
                        isSelectable: false,
                        isExpanded: true,
                        hasChildren: true,
                        childrenCount: 0,
                        children: [],
                    })
                }

                const groupNode = groupsMap.get(groupId)
                if (groupNode?.children) {
                    groupNode.children.push(treeNode)
                    groupNode.childrenCount = groupNode.childrenCount || 0 + 1
                }
            }
        })

        return [...Array.from(groupsMap.values()), ...ungroupedItems]
    } else {
        const newResult = backendArray.map((item) => {
            if (item.id) {
                return {
                    id: `${item.id}`,
                    label: item.label,
                    data: {
                        id: item.id,
                        label: item.label,
                        isActive: item.isActive ?? true,
                    } as SelectableValue,
                    isSelected: true,
                    isSelectable: true,
                    isExpanded: true,
                    hasChildren: false,
                    childrenCount: 0,
                    children: undefined,
                }
            }
        })
        return newResult as TreeNodeData<SelectableValue>[]
    }
}

export const mapToGroupedTreeStructure = <T extends Type>(
    valueResponse: ValueWithGroup[],
    type: T,
    selectedIds: string[],
): TreeNodeData<SelectableValue>[] => {
    if (!valueResponse || valueResponse.length === 0) {
        return []
    }

    const groupsMap = new Map<string | number, TreeNodeData<SelectableValue>>()
    const result: TreeNodeData<SelectableValue>[] = []

    valueResponse.forEach((item) => {
        const childNode: TreeNodeData<SelectableValue> = {
            id: String(item.id),
            label: item.label,
            data: {
                id: item.id,
                label: item.label,
                isActive: item.isActive ?? true,
            } as SelectableValue,
            isSelected: selectedIds.includes(`${item.id}`),
            isSelectable: true,
            isExpanded: true,
            hasChildren: false,
            childrenCount: 0,
            children: [],
        }

        if (!item.groupData || !item.groupData?.id) {
            result.push(childNode)
            return
        }

        const { id: groupId, label: groupName } = item.groupData

        let groupNode = groupsMap.get(groupId)

        if (!groupNode) {
            groupNode = {
                id: String(groupId),
                label: groupName,
                data: {
                    id: groupId,
                    label: groupName,
                    isActive: item.isActive ?? true,
                } as SelectableValue,
                isSelected: false,
                isSelectable: isFieldItemSelect(type),
                isExpanded: true,
                hasChildren: true,
                childrenCount: 0,
                children: [],
            }
            groupsMap.set(groupId, groupNode)
            result.push(groupNode) // push ref so order is preserved
        }

        if (!groupNode.children) {
            groupNode.children = []
        }

        groupNode.children.push(childNode)
        groupNode.childrenCount = (groupNode.childrenCount ?? 0) + 1
    })

    return result
}

export function isFieldItemSelect(type: Type): boolean {
    return ["TF.ItemItemField", "TF.ItemsItemField"].includes(type)
}

export function isFieldTypeSingleSelect(type: Type): boolean {
    switch (type) {
        case "TF.FieldDefinition":
        case "TF.ItemsItemField":
        case "TF.OptionsItemField":
        case "TF.UserGroupsItemField":
        case "TF.UsersItemField":
        case "TF.TagsItemField":
            return false
        case "TF.AccountCodeItemField":
        case "TF.ActivityItemField":
        case "TF.ApplicationItemField":
        case "TF.ApplicationVersionItemField":
        case "TF.BillTypeItemField":
        case "TF.DisplayTypeItemField":
        case "TF.ImpactItemField":
        case "TF.ItemItemField":
        case "TF.OptionItemField":
        case "TF.OrganizationItemField":
        case "TF.ProbabilityItemField":
        case "TF.PriorityItemField":
        case "TF.StatusItemField":
        case "TF.UserGroupItemField":
        case "TF.UserItemField":
            return true
    }
}
